import React, { useEffect, useState } from "react";
import {
    Table,
    Tag,
    Skeleton,
    Typography,
    Input,
    notification,
    Space
} from "antd";
import { getDevicesPaged } from "../services/devicemanagementservice";
import { DeviceDto } from "../types/devicemanagementtypes";
import ScreenshotButton from "../components/devicemanagement/screenshotbutton";
import ViewAppsButton from "../components/devicemanagement/viewappsbutton";
import { Link } from "react-router-dom";
import RebootButton from "../components/devicemanagement/rebootbutton";

const { Title } = Typography;
const { Search } = Input;

const DeviceManagementPage: React.FC = () => {
    const [devices, setDevices] = useState<DeviceDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 5,
        total: 0,
    });
    const [searchTerm, setSearchTerm] = useState<string>("");

    const showError = (msg: string) => {
        notification.error({
            message: "Error",
            description: msg,
            placement: "topRight",
        });
    };

    const fetchDevices = async (page = 1, pageSize = 5, search?: string) => {
        setLoading(true);
        try {
            const { data } = await getDevicesPaged(page, pageSize, search);
            setDevices(data.devices);
            setPagination({
                current: page,
                pageSize,
                total: data.totalCount,
            });
        } catch {
            showError("Failed to load devices");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDevices(1, pagination.pageSize);
    }, []);

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        fetchDevices(1, pagination.pageSize, value);
    };

    const handleTableChange = (newPagination: any) => {
        const { current, pageSize } = newPagination;
        const resetPage = pageSize !== pagination.pageSize ? 1 : current;
        fetchDevices(resetPage, pageSize, searchTerm);
    };

    const columns = [
        {
            title: "Device Name",
            dataIndex: "aliasName",
            key: "aliasName",
            render: (_: string, record: DeviceDto) => (
                <Link
                    to={`/devices/${record.deviceId}`}
                    state={{ aliasName: record.aliasName }}
                >
                    {record.aliasName}
                </Link>
            )
        },
        { title: "Banner", dataIndex: "banner", key: "banner" },
        { title: "Store", dataIndex: "store", key: "store" },
        { title: "Type", dataIndex: "type", key: "type" },
        { title: "ID", dataIndex: "identifier", key: "identifier" },
        { title: "Manufacturer", dataIndex: "manufacturer", key: "manufacturer" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: string | number) => {
                const isOnline =
                    status === 1 || status === "1" || status === "ONLINE";
                return isOnline ? (
                    <Tag color="green">Online</Tag>
                ) : (
                    <Tag color="red">Offline</Tag>
                );
            },
        },
        {
            title: "Provisioned On",
            dataIndex: "provisionedOn",
            key: "provisionedOn",
            render: (date: string) => new Date(date).toLocaleString(),
        },
        {
            title: "Updated On",
            dataIndex: "updatedOn",
            key: "updatedOn",
            render: (date: string) => new Date(date).toLocaleString(),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_: any, device: DeviceDto) => (
                <Space>
                    <ViewAppsButton deviceId={device.deviceId} aliasName={device.aliasName} />
                    <ScreenshotButton deviceId={device.deviceId} aliasName={device.aliasName} />
                    <RebootButton deviceId={device.deviceId} aliasName={device.aliasName} />
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>Devices</Title>
            <Search
                placeholder="Search devices..."
                allowClear
                style={{ marginBottom: 16, maxWidth: 400 }}
                onSearch={handleSearch}
                onChange={(e) => {
                    if (!e.target.value) {
                        setSearchTerm("");
                        fetchDevices(1, pagination.pageSize);
                    }
                }}
            />

            {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <Table
                    rowKey="deviceId"
                    columns={columns}
                    dataSource={devices}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50", "100"],
                        showTotal: (total, range) =>
                            `Showing ${range[0]}–${range[1]} of ${total} devices`,
                    }}
                    onChange={handleTableChange}
                />
            )}
        </div>
    );
};

export default DeviceManagementPage;